import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Loader2, ArrowRight, ArrowLeft, CheckCircle2, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Mon activité", "Mon entreprise", "Coordonnées & légal", "Identité visuelle", "Prestations & tarifs", "Facturation", "URSSAF & comptabilité", "C'est prêt !"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [s, setS] = useState({});
  const [services, setServices] = useState([{ name: "", price: "" }]);
  const [saving, setSaving] = useState(false);
  const [families, setFamilies] = useState([]);
  const [profession, setProfession] = useState("");
  const [professionCustom, setProfessionCustom] = useState("");
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/professions").then((r) => setFamilies(r.data.families || [])).catch(() => {});
    api.get("/business-profile").then((r) => { setProfession(r.data.profession || ""); setProfessionCustom(r.data.profession_custom || ""); }).catch(() => {});
  }, []);

  const finish = async () => {
    setSaving(true);
    try {
      if (profession === "__autre__") {
        if (professionCustom.trim()) await api.put("/business-profile", { profession: "autre", profession_label: professionCustom.trim(), profession_custom: professionCustom.trim(), apply_defaults: true });
      } else if (profession) {
        await api.put("/business-profile", { profession, apply_defaults: true });
      }
      await api.put("/settings", {
        commercial_name: s.commercial_name, owner_name: s.owner_name, address: s.address,
        phone: s.phone, email: s.email, siret: s.siret, weather_city: s.weather_city,
        quote_prefix: s.quote_prefix || "DEV-", invoice_prefix: s.invoice_prefix || "FAC-",
        monthly_objective: s.monthly_objective ? Number(s.monthly_objective) : 0,
        urssaf_rate: s.urssaf_rate != null && s.urssaf_rate !== "" ? Number(s.urssaf_rate) : null,
        cfp_rate: s.cfp_rate != null && s.cfp_rate !== "" ? Number(s.cfp_rate) : null,
        liberatoire_rate: s.liberatoire_rate != null && s.liberatoire_rate !== "" ? Number(s.liberatoire_rate) : null,
        setup_completed: true,
      });
      for (const sv of services) {
        if (sv.name && sv.name.trim()) {
          await api.post("/services", { name: sv.name.trim(), price: sv.price ? Number(sv.price) : null, category: "AUTRES" }).catch(() => {});
        }
      }
      await refresh?.();
      toast.success("Votre espace est prêt !");
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const next = () => setStep((v) => Math.min(v + 1, STEPS.length - 1));
  const prev = () => setStep((v) => Math.max(v - 1, 0));

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-xl">
        <div className="flex flex-col items-center mb-5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center mb-3"><ShieldCheck className="w-7 h-7 text-black" /></div>
          <h1 className="font-heading text-xl font-extrabold text-slate-900">Configurons votre entreprise</h1>
          <p className="text-xs text-slate-500 mt-1">Étape {step + 1} / {STEPS.length} — {STEPS[step]}</p>
          <div className="w-full bg-slate-200 rounded-full h-1.5 mt-3"><div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xl space-y-4" data-testid={`onboarding-step-${step}`}>
          {step === 0 && <>
            <p className="text-xs text-slate-500">Choisissez votre activité : nous adaptons automatiquement les modules et le vocabulaire de l'application à votre métier.</p>
            <Fld label="Votre activité">
              <Select value={profession} onValueChange={setProfession}>
                <SelectTrigger data-testid="ob-profession" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Sélectionner un métier" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-[50vh]">
                  {families.map((f) => (<div key={f.code}>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">{f.label}</div>
                    {f.professions.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                  </div>))}
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Autre</div>
                  <SelectItem value="__autre__">Mon métier n'est pas dans la liste</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
            {profession === "__autre__" && <Fld label="Précisez votre métier"><Input value={professionCustom} onChange={(e) => setProfessionCustom(e.target.value)} data-testid="ob-profession-custom" className="bg-slate-50 border-slate-200" /></Fld>}
          </>}
          {step === 1 && <>
            <Fld label="Nom commercial"><Input value={s.commercial_name || ""} onChange={set("commercial_name")} data-testid="ob-commercial-name" className="bg-slate-50 border-slate-200" placeholder="Ex : Garage Brest Auto" /></Fld>
            <Fld label="Nom du responsable"><Input value={s.owner_name || ""} onChange={set("owner_name")} data-testid="ob-owner-name" className="bg-slate-50 border-slate-200" /></Fld>
          </>}
          {step === 2 && <>
            <Fld label="Adresse"><Input value={s.address || ""} onChange={set("address")} data-testid="ob-address" className="bg-slate-50 border-slate-200" /></Fld>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Téléphone"><Input value={s.phone || ""} onChange={set("phone")} data-testid="ob-phone" className="bg-slate-50 border-slate-200" /></Fld>
              <Fld label="Email"><Input value={s.email || ""} onChange={set("email")} data-testid="ob-email" className="bg-slate-50 border-slate-200" /></Fld>
            </div>
            <Fld label="SIRET"><Input value={s.siret || ""} onChange={set("siret")} data-testid="ob-siret" className="bg-slate-50 border-slate-200" /></Fld>
          </>}
          {step === 3 && <>
            <Fld label="Ville (météo tableau de bord)"><Input value={s.weather_city || ""} onChange={set("weather_city")} data-testid="ob-weather-city" className="bg-slate-50 border-slate-200" placeholder="Ex : Brest" /></Fld>
            <p className="text-xs text-slate-500">Vous pourrez ajouter votre logo et vos couleurs de documents à tout moment dans <b>Paramètres → Design</b>.</p>
          </>}
          {step === 4 && <>
            <p className="text-xs text-slate-500">Ajoutez quelques prestations proposées (facultatif, modifiable ensuite).</p>
            {services.map((sv, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`ob-service-${i}`}>
                <Input value={sv.name} onChange={(e) => setServices(services.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Prestation" data-testid={`ob-service-name-${i}`} className="bg-slate-50 border-slate-200 flex-1" />
                <Input type="number" value={sv.price} onChange={(e) => setServices(services.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} placeholder="Prix €" className="bg-slate-50 border-slate-200 w-24" />
                <Button variant="outline" size="sm" onClick={() => setServices(services.filter((_, idx) => idx !== i))} className="border-red-500/30 text-red-600"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setServices([...services, { name: "", price: "" }])} data-testid="ob-add-service" className="border-slate-200"><Plus className="w-4 h-4 mr-1.5" /> Ajouter</Button>
          </>}
          {step === 5 && <>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Préfixe devis"><Input value={s.quote_prefix || ""} onChange={set("quote_prefix")} data-testid="ob-quote-prefix" placeholder="DEV-" className="bg-slate-50 border-slate-200" /></Fld>
              <Fld label="Préfixe factures"><Input value={s.invoice_prefix || ""} onChange={set("invoice_prefix")} data-testid="ob-invoice-prefix" placeholder="FAC-" className="bg-slate-50 border-slate-200" /></Fld>
            </div>
            <Fld label="Objectif mensuel de CA (€)"><Input type="number" value={s.monthly_objective || ""} onChange={set("monthly_objective")} data-testid="ob-objective" className="bg-slate-50 border-slate-200" /></Fld>
          </>}
          {step === 6 && <>
            <p className="text-xs text-slate-500">Taux estimatifs pour le calcul URSSAF (modifiable dans Paramètres). Ce sont des estimations, aucune déclaration n'est transmise.</p>
            <div className="grid grid-cols-3 gap-3">
              <Fld label="URSSAF %"><Input type="number" value={s.urssaf_rate ?? ""} onChange={set("urssaf_rate")} data-testid="ob-urssaf-rate" className="bg-slate-50 border-slate-200" /></Fld>
              <Fld label="CFP %"><Input type="number" value={s.cfp_rate ?? ""} onChange={set("cfp_rate")} data-testid="ob-cfp-rate" className="bg-slate-50 border-slate-200" /></Fld>
              <Fld label="Vers. lib. %"><Input type="number" value={s.liberatoire_rate ?? ""} onChange={set("liberatoire_rate")} data-testid="ob-lib-rate" className="bg-slate-50 border-slate-200" /></Fld>
            </div>
          </>}
          {step === 7 && <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><Sparkles className="w-7 h-7 text-emerald-600" /></div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Tout est prêt, {s.owner_name || "bienvenue"} !</h3>
            <p className="text-sm text-slate-500">Votre espace <b>{s.commercial_name || "Auto Detail Manager"}</b> est configuré. Vous pouvez commencer à créer vos clients, devis et factures.</p>
          </div>}

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" onClick={prev} disabled={step === 0} data-testid="ob-prev"><ArrowLeft className="w-4 h-4 mr-1.5" /> Précédent</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="ob-next" className="bg-slate-900 text-white hover:bg-slate-800">Continuer <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            ) : (
              <Button onClick={finish} disabled={saving} data-testid="ob-finish" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Accéder à mon espace</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}
